<template>
  <ValidationObserver
    ref="form"
    slim
  >
    <form
      role="form"
      class="form-horizontal"
      name="vm.numberForm"
      novalidate=""
      :disabled="submitting"
      @submit.prevent
    >
      <fieldset>
        <!-- Country -->
        <Field
          :label="$t('NUMBER_COUNTRY')"
          :has-tooltip="true"
          :tooltip-text="$t('NUMBER_COUNTRY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="country"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-3">
                  <CountrySelector
                    v-model="input.country"
                    full
                    :available-countries="availableCountries"
                    name="country"
                    placeholder="Enter country"
                  />
                  <p class="m-t-10" v-t="'NUMBERS_CONTACT_SUPPORT_MESSAGE'" />
                </div>
                <label
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- Bill Card -->
        <Field
          v-if="!isPublisherOrManager"
          :label="$t('NUMBER_CHARGE_ACCOUNT')"
          :has-tooltip="true"
          :tooltip-text="$t('NUMBER_CHARGE_ACCOUNT_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="billCard"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-3">
                  <NzToggle
                    v-model="input.billCard"
                    name="billCard"
                    :disabled="false"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <template v-if="config.hasLocalNumbers">
          <!-- Number Type -->
          <Field
            :label="$t('INPUT_LABEL_NUMBER_TYPE')"
            :has-tooltip="true"
            :tooltip-text="$t('INPUT_LABEL_NUMBER_TYPE_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="numberType"
                rules="required"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4 col-md-3">
                    <input
                      type="hidden"
                      name="numberType"
                      :value="input.numberType"
                    >
                    <MultipleToggle
                      :value="input.numberType"
                      :options="config.numberOptions"
                      @change="input.numberType = $event.value"
                    />
                  </div>
                  <label
                    v-t="'REQUIRED'"
                    class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                  />
                </div>
              </ValidationProvider>
            </template>
          </Field>

          <!-- Prefix -->
          <Field
            :label="$t('INPUT_LABEL_PREFIX')"
            :has-tooltip="true"
            :tooltip-text="$t('INPUT_LABEL_PREFIX_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ errors, classes }"
                ref="prefix"
                rules="no800"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4 col-md-2">
                    <input
                      v-model="input.prefix"
                      placeholder="Optional"
                      type="text"
                      name="incomingQueryStringName"
                      autocomplete="off"
                      class="form-control"
                    >
                    <div
                      v-if="errors.length > 0"
                      class="text-danger"
                    >
                      {{ errors[0] }}
                    </div>
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
        </template>

        <!-- Assign Publisher if user isn't one -->
        <template v-if="!isPublisher">
          <Field
            :label="$t('INPUT_LABEL_PUBLISHER')"
            :has-tooltip="true"
            :tooltip-text="$t('INPUT_LABEL_PUBLISHER_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="publisher"
                rules=""
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4 col-md-3">
                    <publisher-selector
                      v-model="input.publisher"
                      name="publisher"
                      :placeholder="$t('NUMBER_SELECT_AFFILIATE')"
                      :clearable="true"
                      :no-options-text="'none'"
                      :numbers-only="false"
                      :prefix="''"
                      :rules="''"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>

          <!-- Amount -->
          <Field
            :label="$t('NUMBER_MULTIPLE_NUMBERS')"
            :has-tooltip="true"
            :tooltip-text="$t('NUMBER_MULTIPLE_NUMBERS_TT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes, failedRules }"
                ref="amount"
                mode="lazy"
                name="Amount"
                rules="between:1,100"
                slim
              >
                <div :class="classes">
                  <div class="col-sm-4 col-md-2">
                    <input
                      v-model.trim="input.amount"
                      placeholder="1"
                      type="number"
                      pattern="\d*"
                      name="amount"
                      autocomplete="off"
                      class="form-control"
                    >

                    <span class="error">{{ failedRules.between }}</span>
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>
        </template>
      </fieldset>

      <!-- Action Buttons -->
      <div class="row m-b-10">
        <div class="col-sm-7 col-md-7 col-sm-offset-3">
          <!-- Submit -->
          <PrimaryButton
            size="sm"
            type="submit"
            :show-loader-on-form="submitting"
            @click="onSubmit"
          >
            {{ $t('NUMBER_CREATE_BUTTON') }}
          </PrimaryButton>

          <!-- Cancel -->
          <button
            v-if="!isNumberPool && !currentCampaign"
            class="btn btn-danger btn-sm m-l-15"
            type="button"
            @click="$emit('cancel')"
          >
            {{ $t('CANCEL') }}
          </button>
        </div>
      </div>

      <!-- Confirmation of number order -->
      <confirmation-modal
        v-if="showModal"
        ref="modal"
        :title="$t('NUMBER_ORDER_CONFIRMATION_MODAL_TITLE')"
        :allow-modal-to-be-closed="false"
        :cancel-button-text="$t('CANCEL')"
        :confirm-action-text="$t('NUMBER_ORDER_CONFIRMATION_MODAL_CONFIRM_ACTION')"
        :show-header-close-button="false"
        @confirm="$emit('confirm', true)"
        @close="$emit('confirm', false)"
      >
        <div>
          {{ $t('NUMBER_ORDER_CONFIRMATION_MODAL_MESSAGE', {orderAmount: `${input.amount}`}) }}
        </div>
      </confirmation-modal>
    </form>
  </ValidationObserver>
</template>

<script>
import CountrySelector from '@/core/components/CountrySelector/CountrySelector.vue';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle.vue';
import MultiSelect from '@/core/components/MultiSelect/MultiSelect.vue';
import NzToggle from '@/core/components/nzToggle/nzToggle.vue';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown.vue';
import { defineComponent } from 'vue';
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import PublisherSelector from '@/core/components/PublisherSelector/PublisherSelector.vue';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal.vue';
export default defineComponent({
  props: {
    isNumberPool: {
      type: Boolean
    },
    isPublisher: {
      type: Boolean
    },
    isPubManager: {
      type: Boolean
    },
    currentCampaign: {
      type: Object
    },
    availableCountries: {
      type: Array,
      default() {
        return [];
      }
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    input: {
      country: {
        label: 'United States',
        value: 'US',
        hasLocalNumbers: true,
        hasTollFreeNumbers: true
      },
      billCard: true,
      numberType: undefined,
      prefix: undefined,
      publisher: undefined,
      amount: undefined
    },
    showModal: false,
  }),
  components: {
    CountrySelector,
    NzToggle,
    MultipleToggle,
    MultiSelect,
    SearchableDropdown,
    PublisherSelector,
    ConfirmationModal
  },
  computed: {
    config() {
      const {
        needsAllocation,
        needsPublisher,
        hasLocalNumbers,
      } = this;
      return {
        needsAllocation,
        needsPublisher,
        hasLocalNumbers,
        numberOptions: [
          {
            name: 'Toll Free',
            value: true
          },
          {
            name: 'Local',
            value: false
          }
        ]
      };
    },
    isPublisherOrManager() {
      return this.isPublisher || this.isPubManager;
    },
    needsAllocation() {
      return +this.input.amount > 1;
    },
    needsPublisher() {
      return this.input.publisher?.id != null;
    },
    selectedCountryName() {
      return this.input.country?.label;
    },
    selectedCountry() {
      const countries = this.countries;
      if (countries && Array.isArray(countries)) {
        return countries.find(c => c.name === this.selectedCountryName);
      }

    },
    hasLocalNumbers() {
      return this.hasLocalNumbersByCountry(this.input.country);
    },
    output() {
      const {input, config} = this;
      return {
        assignmentSettings: {
          areaCode: input.prefix,
          countryCode: input.country.value,
          chargeAccountIfNeeded: input.billCard,
          isTollFree: input.numberType,
          quantity: input.amount,
          affiliateId: input.publisher?.id
        },
        selectedAffiliate:  input.publisher ? { id: input.publisher.id } : {},
        selectedCountryItem: this.selectedCountry,
        numbersAmount: input.amount,
        config
      };
    },
  },
  methods: {
    hasLocalNumbersByCountry(_country) {
      return _country.hasLocalNumbers && _country.hasTollFreeNumbers;
    },
    async onSubmit() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;


      // Check if we need a number order, display confirmation dialog
      if(this.config.needsAllocation) {
        try {
          await this.showAllocationModal();
        } catch (error) {
          return;
        }
      }

      this.$emit('input', this.output);
    },

    /**
     * Provides a promise for showing / hiding the form
     * Can be used in async functions directly
     */
    showAllocationModal() {
      return new Promise((resolve, reject) => {
        this.showModal = true;
        this.$once('confirm', val => {

          if(!val) {
            reject();
          } else {
            resolve();
          }

          this.showModal = false;
        });
      });
    }
  },
  i18n: {
    messages,
    sharedMessages
  }
});
</script>
